/**
 * Everything behind the sign-in wall, in the shell.
 *
 * The five tabs (UF_00 section 4) live under this route. Onboarding and sign-in deliberately do NOT:
 * a person who is not signed in has no tier badge to show, no notifications to open, and no wallet to
 * navigate to, and rendering the chrome around them would be a promise the app cannot keep.
 *
 * ## The redirect is not the security
 *
 * This bounces a signed-out visitor to /signin because being shown an empty wallet is a bad
 * experience, not because it protects anything. The API refuses every one of these requests with a
 * 401 regardless of what this component decides. If that were not true, the platform's access control
 * would be a `useEffect`.
 *
 * This was the `(app)` route group's layout.tsx. `{children}` became `<Outlet />`, and the bounce is a
 * `<Navigate>` rather than a router.replace inside an effect: the effect form rendered the signed-out
 * shell once before navigating away.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { AppShell } from '@/components/shell';
import { LoadingCard } from '@/components/states';
import { useSession } from '@/lib/session-context';

export function AppLayout() {
  const { signedIn, loading } = useSession();

  // Skeletons, never a blank screen (UF_00 section 6.5). And never the signed-out state either:
  // "we do not know yet" and "you are signed out" are different facts, and showing the second while
  // the first is true throws a signed-in user back to a login form on every slow connection.
  if (loading) {
    return (
      <AppShell>
        <div className="space-y-4">
          <LoadingCard />
          <LoadingCard lines={2} />
        </div>
      </AppShell>
    );
  }

  if (!signedIn) return <Navigate to="/signin" replace />;

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
